/** Contains utilities for determining view-specific properties and drag-and-drop availability for task items. */
// @ts-check
import { computeIndentLevels } from '@/features/tasks/utils/structure/taskHierarchy';

export const VIEW_FLAGS = {
  /** @param {string} view */
  isOrganizeView: (view) => view === 'organize',
  /** @param {string} view */
  isMasterView: (view) => view === 'master',
  /** @param {string} view */
  isTodayView: (view) => view === 'today',
  /** @param {string} view */
  isPlanView: (view) => view === 'plan',
  /** @param {string} view */
  isDashboardView: (view) => view === 'dashboard',
  /** @param {string} view */
  isSubtaskView: (view) => view === 'subtask' || view === 'nested',
};

/** @param {string} view @param {boolean} [isExecutionView] */
export const shouldHideListCheckboxFromTabOrder = (view, isExecutionView = false) =>
  isExecutionView ||
  view === 'plan' ||
  VIEW_FLAGS.isOrganizeView(view) ||
  VIEW_FLAGS.isMasterView(view) ||
  VIEW_FLAGS.isDashboardView(view);

/**
 * Rollup rows stay non-draggable in plan/horizon flat lists. Hierarchical
 * (`indentationType === 'full'`) lists keep parents pickable alongside children.
 * @param {boolean} isRollup
 * @param {string} view
 * @param {string} [indentationType]
 */
const isRollupDragDisabled = (isRollup, view, indentationType = 'none') =>
  indentationType !== 'full' &&
  isRollup &&
  (view === 'plan' || view === 'horizon');

/**
 * @param {string} view
 * @param {boolean} dragEnabled
 * @param {boolean} isSchedulable
 */
const isTodayDragDisabled = (view, dragEnabled, isSchedulable) =>
  view === 'today' && (!dragEnabled || !isSchedulable);

/**
 * @param {string} view
 * @param {boolean} dragEnabled
 * @param {string|null} dragType
 */
const resolveDndType = (view, dragEnabled, dragType) => {
  if (dragType) return dragType;
  if (view === 'today' && dragEnabled) return 'daily-task';
  if (view === 'nested') return 'subtask';
  return 'unscheduled-task';
};

/**
 * @param {Task} task
 * @param {boolean} isDragDisabled
 * @param {string} idPrefix
 */
const buildSortableId = (task, isDragDisabled, idPrefix) => {
  const baseId = task?.id || (isDragDisabled ? 'invalid' : 'invalid-task');
  const prefixedId = idPrefix ? `${idPrefix}:${baseId}` : baseId;
  return isDragDisabled ? `tli-disabled-${prefixedId}` : prefixedId;
};

/**
 * @param {Task} task
 * @param {boolean} isRollup
 * @param {string} view
 * @param {boolean} [dragEnabled]
 * @param {string} [idPrefix]
 * @param {string|null} [dragType]
 * @param {boolean} [isSchedulable]
 * @param {string} [indentationType]
 */
export const getDragConfig = (
  task,
  isRollup,
  view,
  dragEnabled = false,
  idPrefix = '',
  dragType = null,
  isSchedulable = true,
  indentationType = 'none',
) => {
  const isDragDisabled =
    !task ||
    isRollupDragDisabled(isRollup, view, indentationType) ||
    isTodayDragDisabled(view, dragEnabled, isSchedulable);
  const sortableId = buildSortableId(task, isDragDisabled, idPrefix);
  const dndType = resolveDndType(view, dragEnabled, dragType);
  return { isDragDisabled, sortableId, dndType };
};

/**
 * @param {boolean} isExecutionView
 * @param {boolean} isTouchDevice
 * @param {any[]} breadcrumbArray
 */
export const computeBreadcrumbTooltip = (
  isExecutionView,
  isTouchDevice,
  breadcrumbArray,
) => {
  return isExecutionView && !isTouchDevice && breadcrumbArray.length > 0
    ? breadcrumbArray.map((b) => b.title).join(' › ')
    : '';
};

/**
 * @param {any} breadcrumb
 * @returns {any[]}
 */
export const extractBreadcrumbArray = (breadcrumb) => {
  return Array.isArray(breadcrumb)
    ? breadcrumb
    : breadcrumb
      ? [breadcrumb]
      : [];
};

/**
 * @param {Task} task
 * @param {string} indentationType
 * @param {boolean} isExecutionView
 * @param {any[]} breadcrumbArray
 */
export const computeDisplayLevel = (
  task,
  indentationType,
  isExecutionView,
  breadcrumbArray,
) => {
  const { displayLevel: computedLevel } = computeIndentLevels(
    task,
    indentationType,
  );
  return !isExecutionView && breadcrumbArray.length > 0
    ? breadcrumbArray.length
    : computedLevel;
};

/**
 * @param {Task} task
 * @param {{
 *   view: string,
 *   addSubtaskOnParentsOnly?: boolean,
 *   isSmallScreen?: boolean,
 *   isExecutionView?: boolean,
 * }} options
 */
export const canShowAddSubtaskButton = (
  task,
  {
    view,
    addSubtaskOnParentsOnly = false,
    isSmallScreen = false,
    isExecutionView = false,
  },
) => {
  if (isSmallScreen || isExecutionView || !task) return false;

  if (view === 'organize' || view === 'master' || view === 'dashboard')
    return false;

  if (view === 'plan') {
    // Prop name is legacy: enables trailing + on each active row (Plan unscheduled).
    if (!addSubtaskOnParentsOnly) return false;
    return !task.completed;
  }

  return !task.completed;
};
